import React, { useRef } from 'react';
import { Camera, Trash2, Image as ImageIcon } from 'lucide-react';
import { EvidenceItem } from '../../types';

interface EvidenceUploaderProps {
  promptText?: string;
  evidence?: EvidenceItem;
  onChange: (evidence?: EvidenceItem) => void;
  toolName: string;
}

export const EvidenceUploader: React.FC<EvidenceUploaderProps> = ({
  promptText = 'Upload screenshot evidence for this step',
  evidence,
  onChange,
  toolName,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const newEv: EvidenceItem = {
        id: `ev_${Date.now()}`,
        stepId: '',
        sectionId: '',
        tool: toolName,
        dataUrl,
        caption: file.name,
        notes: '',
        timestamp: new Date().toISOString(),
      };
      onChange(newEv);
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    onChange(undefined);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!evidence) return;
    onChange({
      ...evidence,
      notes: e.target.value,
    });
  };

  return (
    <div className="glass-panel" style={{ padding: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: 'var(--accent-indigo)', textTransform: 'uppercase', marginBottom: '12px' }}>
        <Camera size={16} />
        <span>Evidence Verification ({toolName})</span>
      </div>

      <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
        {promptText}
      </p>

      {evidence && evidence.dataUrl ? (
        <div style={{ background: '#020617', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <img
              src={evidence.dataUrl}
              alt="Evidence preview"
              style={{
                width: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-color)',
              }}
            />
            <button
              onClick={handleRemove}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(244, 63, 94, 0.9)',
                border: 'none',
                color: '#fff',
                padding: '6px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              <Trash2 size={14} /> Remove Evidence
            </button>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Auditor Notes / Remarks (Optional)</label>
            <input
              type="text"
              className="input-field"
              value={evidence.notes || ''}
              placeholder="e.g. Confirmed site search shows 269 URLs in Google index."
              onChange={handleNotesChange}
            />
          </div>
        </div>
      ) : (
        <div>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: '30px',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'rgba(2, 6, 23, 0.4)',
              transition: 'var(--transition)',
            }}
            onMouseOver={(e) => (e.currentTarget.style.borderColor = 'var(--accent-blue)')}
            onMouseOut={(e) => (e.currentTarget.style.borderColor = 'var(--border-color)')}
          >
            <ImageIcon size={32} color="var(--accent-blue)" style={{ marginBottom: '10px' }} />
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>
              Click to Upload Screenshot Evidence
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '4px' }}>
              PNG, JPG, WEBP formats supported
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
